import React, { useEffect, useState } from "react";
import { X, Download, Share2 } from "lucide-react";
import { useCompany } from "../contexts/CompanyContext";

// Shows a friendly "Instalar app" banner on mobile browsers that support
// beforeinstallprompt (Chrome/Edge Android). On iOS Safari we show the
// step-by-step since Apple doesn't expose the prompt programmatically.
const DISMISS_KEY = "pwa_install_dismissed_at";
const DISMISS_MS = 1000 * 60 * 60 * 24 * 7; // 7 days

function isIOS() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent || "");
}
function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export default function PwaInstallBanner() {
  const { platformName } = useCompany();
  const [deferred, setDeferred] = useState(null);
  const [iosHelpOpen, setIosHelpOpen] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    try {
      const at = Number(localStorage.getItem(DISMISS_KEY) || 0);
      if (at && Date.now() - at < DISMISS_MS) return;
    } catch (_e) { /* ignore */ }

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS doesn't fire beforeinstallprompt. Show a help-banner instead.
    if (isIOS()) setVisible(true);

    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  if (!visible) return null;

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_e) { /* ignore */ }
    setVisible(false);
  };

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      try { await deferred.userChoice; } catch (_e) { /* ignore */ }
      setDeferred(null);
      setVisible(false);
    } else if (isIOS()) {
      setIosHelpOpen(true);
    }
  };

  return (
    <>
      <div
        className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-sm z-40 rounded-2xl border border-neutral-200 bg-white shadow-xl p-3 flex items-center gap-3 animate-in slide-in-from-bottom-2"
        data-testid="pwa-install-banner"
      >
        <div className="h-10 w-10 rounded-xl bg-company text-[color:var(--company-primary-contrast)] flex items-center justify-center shrink-0">
          <Download size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm">Instala {platformName} en tu teléfono</p>
          <p className="text-xs text-neutral-500">Acceso rápido y como una app.</p>
        </div>
        <button
          type="button"
          onClick={install}
          className="h-9 px-3 rounded-full bg-company text-[color:var(--company-primary-contrast)] text-xs font-semibold shrink-0"
          data-testid="pwa-install-btn"
        >
          Instalar
        </button>
        <button
          type="button"
          onClick={dismiss}
          className="h-8 w-8 rounded-full hover:bg-neutral-100 flex items-center justify-center shrink-0"
          aria-label="Cerrar"
          data-testid="pwa-dismiss-btn"
        >
          <X size={14} />
        </button>
      </div>

      {iosHelpOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-end md:items-center justify-center p-4"
          onClick={() => setIosHelpOpen(false)}
        >
          <div
            className="bg-white rounded-2xl p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-display font-extrabold text-xl">Agrega {platformName} a tu inicio</h3>
            <ol className="mt-3 space-y-2 text-sm text-neutral-700">
              <li>1. Toca el botón <span className="inline-flex items-center gap-1 font-semibold"><Share2 size={14} /> Compartir</span> de Safari.</li>
              <li>2. Desliza y elige <b>Agregar a pantalla de inicio</b>.</li>
              <li>3. Toca <b>Agregar</b>. Listo 🎉</li>
            </ol>
            <button
              type="button"
              onClick={() => { setIosHelpOpen(false); dismiss(); }}
              className="mt-5 w-full h-11 rounded-xl bg-black text-white text-sm font-semibold"
            >
              Entendido
            </button>
          </div>
        </div>
      )}
    </>
  );
}
